import React, { useCallback, useState } from 'react';
import { Lightbulb, Send } from 'lucide-react';
import SectionShell from '../plan/SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

const CATEGORIES = ['General', 'Safety', 'Preparation', 'Communication', 'Tooling', 'Coordination', 'Procedure'];

/**
 * LessonsLearnedSection — Phase E: append-only lessons log.
 * Once added, a lesson is immutable (matches audit-log integrity policy).
 */
export default function LessonsLearnedSection({ draftId, closeoutState, ownerEmail, onChanged, isReadOnly }) {
  const lessons = closeoutState?.lessons_learned || [];
  const [text, setText] = useState('');
  const [category, setCategory] = useState('General');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    if (!text.trim() || !draftId) return;
    setBusy(true);
    try {
      await jobBriefApi.addLessonLearned(draftId, {
        text: text.trim(),
        category,
        added_by: ownerEmail,
      });
      setText('');
      setCategory('General');
      onChanged?.();
    } finally { setBusy(false); }
  }, [text, category, draftId, ownerEmail, onChanged]);

  return (
    <SectionShell
      icon={Lightbulb}
      title="Lessons Learned"
      subtitle="What would you tell the crew running this job tomorrow? Every entry is stamped to the audit log."
      color="#facc15"
      badge={lessons.length ? `${lessons.length} captured` : 'None'}
      testId="jbv2-section-lessons"
    >
      {lessons.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-lessons-empty">
          No lessons captured yet.
        </div>
      )}
      {lessons.map((l) => (
        <div key={l.id} className="jbv2-row" data-testid={`jbv2-lessons-row-${l.id}`}>
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat">{l.category}</div>
            <div className="jbv2-row-note">{l.text}</div>
            <div className="jbv2-hp-row-meta">
              {l.actor} · {l.created_at && new Date(l.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      ))}

      {!isReadOnly && (
        <div className="jbv2-add-row" style={{ flexDirection: 'column', alignItems: 'stretch' }} data-testid="jbv2-lessons-form">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="jbv2-lessons-category"
              aria-label="Category"
              style={{ maxWidth: 200 }}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <textarea
            className="jbv2-field-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Bring an extra grounding chain — the on-site kit was 6ft short of the far bay."
            rows={3}
            data-testid="jbv2-lessons-text"
            aria-label="Lesson"
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || !text.trim()}
            data-testid="jbv2-lessons-add-btn"
          >
            <Send size={13} /> Add lesson
          </button>
        </div>
      )}
    </SectionShell>
  );
}
